import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import {
  Alert,
  Backdrop,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import update from 'immutability-helper';
import * as _ from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {fromDisplaySpeed, toDisplaySpeed} from '../../../constants/common';
import {useAppSelector} from '../../../hooks/redux';
import {DashboardPanelData} from '../../../interfaces/Dashboard';
import {
  HazardAIDetectionTotalQuery,
  HazardAIDetectionTotalResponse,
} from '../../../interfaces/HazardAIDetectionTotal';
import {useDashboardDates} from '../../../utils/dashboardDate';
import {
  DetectionDurationPreset,
  resolveDurationPreset,
} from '../../../utils/dateRange';
import NumberTextField from '../../common/NumberTextField';
import {usePanelFilter} from '../../dashboards/entities/DashboardEntityContext';
import {DateRangeSelect} from '../../selectors/DateRangeSelect';
import {DetectionDurationSelect} from '../../selectors/DetectionDurationSelect';
import {SafeyeNodeSelector} from '../../selectors/SafeyeNodeSelector';
import {SitesSelect} from '../../selectors/SitesSelect';
import {DashboardPanelActionsSlot} from '../DashboardPanelActionsSlot';
import {HeatmapObjectSelector} from '../HazardHeatmap/HeatmapObjectSelector';

interface Props {
  value?: DashboardPanelData;
  onUpdate?: (value?: DashboardPanelData) => void;
}

interface EventTotalKpiProps extends Props {
  /** When provided, counts only events in this zone. Omit for all zones. */
  zone?: 'hazard' | 'warning';
  /** Overline label shown inside the KPI card, e.g. "Total Hazard Events". */
  label: string;
  /** Error code prefix for API error messages. */
  errorId: string;
  /**
   * Controls which secondary filter is shown alongside Sites/Machines/Period.
   * 'object' (default): shows the object-type selector.
   * 'speed': shows a speed-threshold input; speed_min is sent to the backend.
   */
  filterMode?: 'object' | 'speed';
}

interface AllParams {
  current: HazardAIDetectionTotalQuery;
  previous: HazardAIDetectionTotalQuery;
  /** Number of days in the current period (inclusive) */
  periodDays: number;
  /** Formatted range strings for the read-only caption */
  currentLabel: string;
  previousLabel: string;
}

const DATE_DISPLAY_FMT = 'MMM D';
const DATE_STORAGE_FMT = 'YYYY-MM-DD';

export const EventTotalKpi = (props?: EventTotalKpiProps) => {
  const zone = props?.zone;
  const label = props?.label ?? 'Total Detections';
  const errorId = props?.errorId ?? 'STRA_f4a2b8c1';
  const filterMode = props?.filterMode ?? 'object';

  const {filter, setFilter} = usePanelFilter();

  // When this panel participates in the dashboard-wide date range, use the
  // dashboard dates as the "current" window and compute "previous" from it.
  const dashboardDates = useDashboardDates(props?.value?.detachedFromDashboardDate);

  const filtersCollapsed = !!(filter.params?.filtersCollapsed);
  const speedFormat = useAppSelector(({app}) => app.speedFormat);

  const [fetchedCurrentTotal, setFetchedCurrentTotal] = useState<number>();
  const [fetchedPrevTotal, setFetchedPrevTotal] = useState<number>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  // ------------------------------------------------------------------
  // Build query params for both periods
  // ------------------------------------------------------------------
  const allParams = useMemo<AllParams>(() => {
    const preset: DetectionDurationPreset =
      (filter.params?.duration_preset as DetectionDurationPreset | undefined) ??
      (props?.value?.params?.duration_preset as DetectionDurationPreset | undefined) ??
      'week';

    const sharedFilters: Partial<HazardAIDetectionTotalQuery> = {
      safeye_node_id: _.has(filter.params, 'safeye_node_ids')
        ? filter.params?.safeye_node_ids
        : props?.value?.params?.safeye_node_id,
      site_id: _.has(filter.params, 'site_ids')
        ? (filter.params?.site_ids as unknown as number[])
        : (props?.value?.params?.site_id as unknown as number[]),
      ...(filterMode === 'speed'
        ? {
            speed_min: ((filter.params?.speed_min ??
              props?.value?.params?.speed_min ??
              10) as number),
          }
        : {object: (filter.params?.object ?? undefined) as string | undefined}),
      ...(zone ? {zone} : {}),
    };

    // Dashboard-driven branch: when the dashboard date picker has applied a
    // range to this panel, use it as the "current" window. The "previous"
    // window is always the equal-length range immediately before current.
    // This takes priority over the panel's own duration preset.
    if (dashboardDates) {
      const {date_start, date_end} = dashboardDates;
      const N = dayjs(date_end).diff(dayjs(date_start), 'day') + 1;
      const prevEnd = dayjs(date_start).subtract(1, 'day');
      const prevStart = prevEnd.subtract(N - 1, 'day');
      const fmt = (d: string) => dayjs(d).format(DATE_DISPLAY_FMT);
      return {
        current: {date_start, date_end, ...sharedFilters},
        previous: {
          date_start: prevStart.format(DATE_STORAGE_FMT),
          date_end: prevEnd.format(DATE_STORAGE_FMT),
          ...sharedFilters,
        },
        periodDays: N,
        currentLabel: `${fmt(date_start)} – ${fmt(date_end)}`,
        previousLabel: `${fmt(prevStart.format(DATE_STORAGE_FMT))} – ${fmt(prevEnd.format(DATE_STORAGE_FMT))}`,
      };
    }

    if (preset !== 'custom') {
      // Relative duration: always recompute from today — never read stale stored dates.
      const resolved = resolveDurationPreset(preset)!;
      const fmt = (d: string) => dayjs(d).format(DATE_DISPLAY_FMT);
      return {
        current: {
          date_start: resolved.current.date_start,
          date_end: resolved.current.date_end,
          ...sharedFilters,
        },
        previous: {
          date_start: resolved.previous.date_start,
          date_end: resolved.previous.date_end,
          ...sharedFilters,
        },
        periodDays: resolved.days,
        currentLabel: `${fmt(resolved.current.date_start)} – ${fmt(resolved.current.date_end)}`,
        previousLabel: `${fmt(resolved.previous.date_start)} – ${fmt(resolved.previous.date_end)}`,
      };
    }

    // Custom: read stored ranges with today-fallbacks.
    const date_start =
      filter.params?.date_start ??
      props?.value?.params?.date_start ??
      dayjs().format(DATE_STORAGE_FMT);
    const date_end =
      filter.params?.date_end ??
      props?.value?.params?.date_end ??
      dayjs().format(DATE_STORAGE_FMT);

    const N = dayjs(date_end).diff(dayjs(date_start), 'day') + 1;

    // Compare period: auto-compute as N days immediately before, overridden when
    // the user has explicitly pinned compare dates.
    const autoPrevEnd = dayjs(date_start).subtract(1, 'day');
    const autoPrevStart = autoPrevEnd.subtract(N - 1, 'day');

    const prev_date_start =
      filter.params?.prev_date_start ??
      props?.value?.params?.prev_date_start ??
      autoPrevStart.format(DATE_STORAGE_FMT);
    const prev_date_end =
      filter.params?.prev_date_end ??
      props?.value?.params?.prev_date_end ??
      autoPrevEnd.format(DATE_STORAGE_FMT);

    const fmt = (d: string) => dayjs(d).format(DATE_DISPLAY_FMT);
    return {
      current: {date_start, date_end, ...sharedFilters},
      previous: {
        date_start: prev_date_start,
        date_end: prev_date_end,
        ...sharedFilters,
      },
      periodDays: N,
      currentLabel: `${fmt(date_start)} – ${fmt(date_end)}`,
      previousLabel: `${fmt(prev_date_start)} – ${fmt(prev_date_end)}`,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filter.params,
    dashboardDates?.date_start,
    dashboardDates?.date_end,
    props?.value?.detachedFromDashboardDate,
  ]);

  const lastPersisted = useRef<unknown>(props?.value?.params);
  useEffect(() => {
    if (allParams) {
      fetchData(allParams);
      if (!_.isEqual(filter.params, lastPersisted.current)) {
        lastPersisted.current = filter.params;
        props?.onUpdate?.({...props.value, params: filter.params});
      }
    }
  }, [allParams]);

  const fetchData = useCallback(
    async (params: AllParams) => {
      setFetchedInProgress(true);
      setFetchedErrors([]);
      try {
        const endpoint = `${apiBaseUrl}/hazard-ai/detection-total`;
        const [currentResp, prevResp] = await Promise.all([
          API.get<HazardAIDetectionTotalResponse>(endpoint, {
            params: params.current,
          }),
          API.get<HazardAIDetectionTotalResponse>(endpoint, {
            params: params.previous,
          }),
        ]);
        setFetchedCurrentTotal(currentResp.data.total);
        setFetchedPrevTotal(prevResp.data.total);
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, errorId);
        setFetchedErrors(messages);
      }
      setFetchedInProgress(false);
    },
    [allParams]
  );

  // ------------------------------------------------------------------
  // Delta computation
  // ------------------------------------------------------------------
  const delta = useMemo(() => {
    if (
      fetchedCurrentTotal === undefined ||
      fetchedPrevTotal === undefined ||
      fetchedPrevTotal === 0
    ) {
      return null;
    }
    return ((fetchedCurrentTotal - fetchedPrevTotal) / fetchedPrevTotal) * 100;
  }, [fetchedCurrentTotal, fetchedPrevTotal]);

  // Current preset, read once for render branching.
  const activePreset: DetectionDurationPreset =
    (filter.params?.duration_preset as DetectionDurationPreset | undefined) ??
    (props?.value?.params?.duration_preset as DetectionDurationPreset | undefined) ??
    'week';

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <Box
      display="flex"
      flexDirection="column"
      height="calc(100% - 38px)"
      width="100%"
      overflow="hidden"
    >
      <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <DashboardPanelActionsSlot>
        <Tooltip title={filtersCollapsed ? 'Show Filters' : 'Hide Filters'}>
          <IconButton
            size="small"
            color="primary"
            onClick={() =>
              setFilter(
                update(filter, {
                  params: {
                    filtersCollapsed: {$set: !filtersCollapsed},
                  },
                })
              )
            }
          >
            {filtersCollapsed ? (
              <FilterAltIcon fontSize="small" />
            ) : (
              <FilterAltOffIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </DashboardPanelActionsSlot>

      {/* Filters */}
      {!filtersCollapsed && (
      <Box display="flex" flexWrap="wrap" gap={1} py={1} alignItems="flex-start">
        <Box maxWidth={400} minWidth={200}>
          <SitesSelect
            size="small"
            multiple
            fullWidth
            value={allParams.current.site_id}
            onChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    site_ids: {$set: v as number[]},
                  },
                })
              );
            }}
          />
        </Box>
        <Box maxWidth={400} minWidth={200}>
          <SafeyeNodeSelector
            size="small"
            multiple
            fullWidth
            value={allParams.current.safeye_node_id as unknown as number[]}
            onChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    safeye_node_ids: {$set: v as unknown as string[]},
                  },
                })
              );
            }}
          />
        </Box>

        {filterMode === 'object' ? (
          <Box minWidth={180}>
            <HeatmapObjectSelector
              size="small"
              fullWidth
              value={allParams.current.object ?? null}
              onChange={(v) => {
                setFilter(
                  update(filter, {
                    params: {
                      object: {$set: v ?? undefined},
                    },
                  })
                );
              }}
            />
          </Box>
        ) : (
          <Box minWidth={180}>
            <NumberTextField
              size="small"
              fullWidth
              min={0}
              step={1}
              label={`Speed Threshold (${speedFormat.toUpperCase()})`}
              value={Math.round(
                toDisplaySpeed(allParams.current.speed_min ?? 10, speedFormat)
              )}
              onChange={(v) => {
                setFilter(
                  update(filter, {
                    params: {
                      speed_min: {
                        $set: fromDisplaySpeed(v ?? 0, speedFormat),
                      },
                    },
                  })
                );
              }}
            />
          </Box>
        )}

        {/* Duration selector */}
        <Box minWidth={180}>
          <DetectionDurationSelect
            value={activePreset}
            size="small"
            fullWidth
            onChange={(preset) => {
              setFilter(
                update(filter, {
                  params: {
                    duration_preset: {$set: preset},
                    // Clear custom date keys — relative presets recompute from today.
                    date_start: {$set: null},
                    date_end: {$set: null},
                    prev_date_start: {$set: null},
                    prev_date_end: {$set: null},
                  },
                })
              );
              // Detach from the dashboard date so the panel uses its own preset.
              if (dashboardDates) {
                props?.onUpdate?.({...props?.value, detachedFromDashboardDate: true});
              }
            }}
          />

          {/* Read-only range caption for relative presets */}
          {(dashboardDates || activePreset !== 'custom') && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mt={0.5}
            >
              {allParams.currentLabel} vs {allParams.previousLabel}
            </Typography>
          )}
        </Box>

        {/* Custom date pickers — shown only when preset is 'custom' and not
            following the dashboard (which always drives the current range) */}
        {activePreset === 'custom' && !dashboardDates && (
          <>
            <Box maxWidth={400}>
              <DateRangeSelect
                value={[
                  dayjs(allParams.current.date_start).toDate(),
                  dayjs(allParams.current.date_end).toDate(),
                ]}
                size="small"
                maxDays={366}
                labels={['Period Start', 'Period End']}
                onChange={(v) => {
                  // When the user edits the current range, auto-clear compare
                  // so the equal-length prior window is auto-computed — unless
                  // the user has already pinned a compare range.
                  const compareIsPinned = !!(
                    filter.params?.prev_date_start || filter.params?.prev_date_end
                  );
                  setFilter(
                    update(filter, {
                      params: {
                        duration_preset: {$set: 'custom'},
                        date_start: {
                          $set: v?.[0]
                            ? dayjs(v[0]).format(DATE_STORAGE_FMT)
                            : undefined,
                        },
                        date_end: {
                          $set: v?.[1]
                            ? dayjs(v[1]).format(DATE_STORAGE_FMT)
                            : undefined,
                        },
                        ...(!compareIsPinned
                          ? {
                              prev_date_start: {$set: null},
                              prev_date_end: {$set: null},
                            }
                          : {}),
                      },
                    })
                  );
                }}
              />
            </Box>
            <Box maxWidth={400}>
              <DateRangeSelect
                value={[
                  dayjs(allParams.previous.date_start).toDate(),
                  dayjs(allParams.previous.date_end).toDate(),
                ]}
                size="small"
                maxDays={366}
                labels={['Compare Start', 'Compare End']}
                onChange={(v) => {
                  setFilter(
                    update(filter, {
                      params: {
                        duration_preset: {$set: 'custom'},
                        prev_date_start: {
                          $set: v?.[0]
                            ? dayjs(v[0]).format(DATE_STORAGE_FMT)
                            : undefined,
                        },
                        prev_date_end: {
                          $set: v?.[1]
                            ? dayjs(v[1]).format(DATE_STORAGE_FMT)
                            : undefined,
                        },
                      },
                    })
                  );
                }}
              />
            </Box>
          </>
        )}
      </Box>
      )}

      {/* Errors */}
      {fetchedErrors.map((error, idx) => (
        <Alert
          key={idx}
          severity="error"
          onClose={() => allParams && fetchData(allParams)}
        >
          {error}
        </Alert>
      ))}

      {/* KPI Content */}
      <Box
        display="flex"
        flexDirection="column"
        flex={1}
        overflow="auto"
        px={2}
        py={1}
        gap={0.5}
      >
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{letterSpacing: 2}}
        >
          / {label}
        </Typography>

        <Typography
          variant="h2"
          fontWeight={600}
          lineHeight={1.1}
          sx={{fontSize: {xs: '2.5rem', md: '3.5rem'}}}
        >
          {fetchedCurrentTotal !== undefined
            ? fetchedCurrentTotal.toLocaleString()
            : '—'}
        </Typography>

        <Box display="flex" alignItems="center" gap={0.5} minHeight={24}>
          {delta !== null ? (
            <>
              {delta <= 0 ? (
                <ArrowDownwardIcon
                  fontSize="small"
                  sx={{color: 'success.main'}}
                />
              ) : (
                <ArrowUpwardIcon
                  fontSize="small"
                  sx={{color: 'error.main'}}
                />
              )}
              <Typography
                variant="body2"
                sx={{color: delta <= 0 ? 'success.main' : 'error.main'}}
              >
                {Math.abs(delta).toFixed(1)}% vs previous{' '}
                {allParams.periodDays}{' '}
                {allParams.periodDays === 1 ? 'day' : 'days'}
              </Typography>
            </>
          ) : (
            fetchedPrevTotal === 0 &&
            fetchedCurrentTotal !== undefined && (
              <Typography variant="body2" color="text.secondary">
                No data for comparison period
              </Typography>
            )
          )}
        </Box>
      </Box>
    </Box>
  );
};
